import {
  DEFAULT_FILTER_PRIORITY,
  type AnyBlock,
  type MutableFilterBlock,
  type FilterQuery,
  type FilterValueData,
  type ValueRowProps,
  type IndexedBlockValueItem,
} from '@github-ui/filter'
import {BaseUserFilterProvider, type User} from '@github-ui/filter/providers'
import {getFilterValue, getUnescapedFilterValue} from '@github-ui/filter/utils'
import {ValueIcon} from '@github-ui/filter/utils/ValueIcon'
import {PersonIcon} from '@primer/octicons-react'
import {AvatarType} from '@github-ui/filter'
import {verifiedFetch} from '@github-ui/verified-fetch'
import type {User as InsightsUser} from '@github-ui/user-selector'
import {singleValueFilterTypes} from '../SecurityProductsFilterBar'

interface InsightsActorsResponse {
  actors: InsightsUser[]
}

export class UserFilterProvider extends BaseUserFilterProvider {
  #actorsEndpoint: string
  #cachedActors: User[] | null = null
  #fetchPromise: Promise<User[]> | null = null

  constructor(actorsEndpoint: string = 'insights/actors') {
    super(
      {
        displayName: 'User',
        key: 'user',
        icon: PersonIcon,
        description: 'Specify the user',
        priority: DEFAULT_FILTER_PRIORITY,
      },
      {
        showAtMe: false,
        includeBots: true,
      },
      {
        filterTypes: singleValueFilterTypes,
      },
    )
    this.#actorsEndpoint = actorsEndpoint
  }

  async #fetchActors(): Promise<User[]> {
    if (this.#cachedActors !== null) {
      return this.#cachedActors
    }

    if (this.#fetchPromise !== null) {
      return this.#fetchPromise
    }

    this.#fetchPromise = (async () => {
      try {
        const response = await verifiedFetch(this.#actorsEndpoint)
        if (!response.ok) {
          this.#cachedActors = []
          return []
        }

        const data = (await response.json()) as InsightsActorsResponse
        const actors: User[] = (data.actors || []).map(actor => ({
          login: actor.login,
          name: actor.name ?? actor.login,
          avatarUrl: actor.primaryAvatarUrl,
        }))

        this.#cachedActors = actors
        return actors
      } catch {
        this.#cachedActors = []
        return []
      } finally {
        this.#fetchPromise = null
      }
    })()

    return this.#fetchPromise
  }

  override async fetchSuggestions(
    value: string,
    _block: AnyBlock | MutableFilterBlock,
    _filterQuery: FilterQuery,
  ): Promise<User[] | null> {
    const actors = await this.#fetchActors()

    if (!value) {
      return actors
    }

    const lowerValue = value.toLowerCase()
    return actors.filter(
      actor => actor.login.toLowerCase().includes(lowerValue) || (actor.name ?? '').toLowerCase().includes(lowerValue),
    )
  }

  override validateValue(
    _filterQuery: FilterQuery,
    value: IndexedBlockValueItem,
    _user: User | null,
  ): false | Partial<IndexedBlockValueItem> | null | undefined {
    const extractedValue = getUnescapedFilterValue(value.value)
    if (!extractedValue) return false

    const actor = this.#cachedActors?.find(a => a.login.toLowerCase() === extractedValue.toLowerCase())

    if (actor) {
      return {
        avatar: actor.avatarUrl ? {url: actor.avatarUrl, type: AvatarType.User} : undefined,
        value: extractedValue,
        displayName: actor.name,
      }
    }

    if (this.#cachedActors === null) {
      return {value: extractedValue}
    }

    return false
  }

  override getValueRowProps(value: FilterValueData): ValueRowProps {
    return {
      text: value.displayName ?? getFilterValue(value.value) ?? '',
      description: value.description,
      descriptionVariant: 'inline',
      leadingVisual: ValueIcon({value, providerIcon: this.icon}),
    }
  }
}
